/**
 * @file The one place a `/patient/booking-form` link is built.
 * @module utils/healthcare/bookingLink
 *
 * The booking-link twin of `utils/forms/patientFormLink`. Every copy of a booking link a patient
 * can receive — the QR on the practitioner's screen, the emailed copy, the SMS — comes from here,
 * because the patient only ever sees whichever copy reached them and they must all be the same
 * URL. A second interpolation site anywhere is how "I scanned it" and "I got your text" become
 * two different links.
 *
 * ⚠️ THE TOKEN IS NEVER INTERPOLATED AS `undefined`.
 * The forms builder can OMIT a missing token, because `EnsureFormAccessToken` forgives an absent
 * one during the rollout grace period. `EnsureBookingLinkToken` has **no grace period by design**
 * — a missing or wrong token is a hard 404 and there is nothing here that forgives it. So a
 * tokenless URL is not a degraded link, it is a broken one that looks fine on screen. Building
 * one throws instead: the caller's error path is already the right place for that failure.
 */

import { ENV } from "../../config/runtimeEnv";

export const buildPatientBookingLink = (token: string): string => {
	if (!token) {
		throw new Error("A patient booking link cannot be built without a token");
	}

	return `${ENV.VITE_FRONTEND_BASE_URL}/patient/booking-form?t=${encodeURIComponent(token)}`;
};
